import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ConsultantUtilisationList,
  TeamStatusToday,
  useCanOpenLoggedUtilisation,
  type ConsultantUtilisationRow,
} from '../../dashboard-widgets';
import { usePermissions } from '../../../contexts/AuthContext';
import { hasPermission, P } from '../../../utils/permissions';
import { employeeDisplayName } from '../../../utils/employeeName';
import type { Consultant } from '../types';
import type { TeamStatusTodayCounts } from '../../dashboard-widgets/types';

interface TeamSectionProps {
  loading: boolean;
  consultants: Consultant[];
  isConsultantRole: (jobTitle: string) => boolean;
  teamStatusToday: TeamStatusTodayCounts | undefined;
  onViewAllPlanning: () => void;
}

/**
 * `Team` section — prototype port:
 *   - Left (1.2fr): `ConsultantUtilisationList` row-list (everyone, sortable).
 *   - Right (1fr) : `TeamStatusToday` 5-count widget, shown to anybody the
 *                   SERVER served counts to — a manager with reports, or a
 *                   holder of an org-wide timesheet key. When there are none,
 *                   the right column collapses and the list takes the full
 *                   width.
 *
 * Section gate (`VIEW_REPORTS_OPERATIONAL || VIEW_TIMESHEETS_ALL`) lives
 * in `sections.tsx`, not here.
 */
export function TeamSection({
  loading,
  consultants,
  isConsultantRole,
  teamStatusToday,
  onViewAllPlanning,
}: TeamSectionProps) {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  /**
   * The SERVER decides who sees the team-status widget — it omits the payload
   * entirely for a caller who can see nobody (no reports and no org-wide
   * timesheet key). Re-deriving that rule here from `APPROVE_TIMESHEETS` would
   * be a second answer to the question, and it was the wrong one: it hid the
   * widget from managers, who are exactly who it is for.
   *
   * `permissions` is still read for the loading case, where the payload has not
   * arrived and something has to decide whether to hold the column open.
   */
  const mayHaveTeamStatus = hasPermission(permissions, P.APPROVE_TIMESHEETS);
  const showTeamStatus = teamStatusToday !== undefined || (loading && mayHaveTeamStatus);
  const canOpenLoggedUtilisation = useCanOpenLoggedUtilisation();

  // Filter to active consultants (role-based) with a real utilisation %.
  const rows: ConsultantUtilisationRow[] = consultants
    .filter(
      (c) =>
        isConsultantRole(c.jobTitle || '') &&
        c.status === 'active' &&
        c.utilisation != null,
    )
    .map((c) => ({
      id: c.id,
      name: employeeDisplayName(c) || 'Unknown',
      avatarUrl: c.avatarUrl,
      utilisation: Math.round(c.utilisation ?? 0),
      band: c.band ?? 'unassigned',
      onLeave: c.onLeave ?? false,
      leaveDays: c.leaveDays ?? 0,
      allocationCount: c.allocationCount ?? 0,
    }));

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: showTeamStatus
          ? { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(0, 1fr)' }
          : '1fr',
        gap: 2,
        alignItems: 'start',
      }}
    >
      <ConsultantUtilisationList
        rows={rows}
        totalCount={rows.length}
        maxRows={8}
        loading={loading}
        onViewAll={onViewAllPlanning}
        showLoggedUtilisationLink={canOpenLoggedUtilisation}
      />
      {showTeamStatus ? (
        <TeamStatusToday
          counts={teamStatusToday}
          loading={loading}
          // The compliance report is where a reminder is actually composed —
          // which week, who exactly, and when each was last chased. Navigating
          // there beats a dialog that asks for that decision with none of the
          // evidence on screen.
          onReviewCompliance={() => navigate('/timesheets?tab=reports')}
        />
      ) : null}
    </Box>
  );
}
